/**
 * author: Ananthapadmanabhan
 */
import React, { useEffect, useRef, useState } from "react";
import { Button, Modal, OverlayTrigger, Tooltip } from "react-bootstrap";
import { Typeahead } from "react-bootstrap-typeahead";
import { withTranslation } from "react-i18next";
import { BiChevronLeft, BiChevronRight } from "react-icons/bi";
import { MdAddCircleOutline, MdBackspace, MdContentPaste } from "react-icons/md";
import { toast } from "react-toastify";
import { EquationBuilderFor, LS_AUTHORITY_KEY, USER_ROLE } from "../../../../core/constants/common";
import ESGDataSetService from "../../../../core/service/ESGDataSetService";
import EquationService from "../../../../core/service/EquationService";
import KPIService from "../../../../core/service/KPIService";
import CreateDataPoint from "../CreateDataPoint/CreateDataPoint";
import "./EquationBuilder.scss";
import StandardService from "../../../../core/service/StandardService";

/**
 * Removes all whitespace from equation strings so pasted input matches calculator output
 * and backend payloads stay consistent (e.g. "A + B" -> "A+B").
 */
export function stripEquationWhitespace(s) {
  if (s == null || s === "") {
    return s;
  }
  return String(s).replace(/\s+/g, "");
}

function EquationBuilder({
  t,
  stdId,
  metricId,
  topicId,
  discardBtnClick,
  equationObj,
  updatedEquationObj,
  buildFor,
  forCompany = false,
  topicIds = []
}) {

  const authority = JSON.parse(localStorage.getItem(LS_AUTHORITY_KEY));
  const [equation, setEquation] = useState('');
  const [equationwithid, setEquationWithId] = useState('');
  const [configDPdata, setConfigDPData] = useState([]);
  const [configMetricdata, setConfigMetricData] = useState([]);
  const [configGDData, setConfigGDData] = useState([]);
  const [editid, setEditId] = useState('');
  const [dpModalDisplay, setDPModalDisplay] = useState(false);
  const [selectedDPs, setSelectedDPs] = useState([]);
  const [selectedMetricIds, setSelectedMetricIds] = useState([]);
  const [isMetricEmission, setIsMetricEmission] = useState(false);
  const [isDPEmission, setIsDPEmission] = useState(false);
  const [focusedButtonIndex, setFocusedButtonIndex] = useState(0);
  const [isKeyboardMode, setIsKeyboardMode] = useState(false);
  const [validationStatus, setValidationStatus] = useState({ isValid: true, message: '' });
  const [editingOperator, setEditingOperator] = useState(null);
  const [operatorReplacement, setOperatorReplacement] = useState('');

  // Refs for keyboard navigation
  const equationDisplayRef = useRef(null);
  const buttonRefs = useRef([]);
  const containerRef = useRef(null);

  useEffect(() => {
    getConfigData();
  }, []);

  // Validate equation whenever it changes (normalization matches save/back-end payload)
  useEffect(() => {
    if (equation) {
      const validation = validateEquation(equation);
      setValidationStatus(validation);
    } else {
      setValidationStatus({ isValid: true, message: '' });
    }
  }, [equation]);

  // Initialize button refs array
  useEffect(() => {
    // Calculate total number of buttons (numbers, operators, functions)
    const totalButtons = 28; // 0-9, +, -, *, /, %, (, ), ., =, IF, THEN, ELSE, <, <=, >=, >, AC, backspace
    buttonRefs.current = buttonRefs.current.slice(0, totalButtons);
  }, []);

  const navigateButtons = (direction) => {
    const buttonsPerRow = 4;
    const totalButtons = buttonRefs.current.length;
    let newIndex = focusedButtonIndex;

    switch (direction) {
      case 'left':
        newIndex = focusedButtonIndex > 0 ? focusedButtonIndex - 1 : totalButtons - 1;
        break;
      case 'right':
        newIndex = focusedButtonIndex < totalButtons - 1 ? focusedButtonIndex + 1 : 0;
        break;
      case 'up':
        newIndex = focusedButtonIndex >= buttonsPerRow ? focusedButtonIndex - buttonsPerRow : focusedButtonIndex;
        break;
      case 'down':
        newIndex = focusedButtonIndex + buttonsPerRow < totalButtons ? focusedButtonIndex + buttonsPerRow : focusedButtonIndex;
        break;
      default:
        break;
    }

    setFocusedButtonIndex(newIndex);
    if (buttonRefs.current[newIndex]) {
      buttonRefs.current[newIndex].focus();
    }
  };

  const getConfigData = () => {
    if (buildFor === EquationBuilderFor.ESG_ANALYTICS) {
      loadAllMetricsAndDatapointsForEnalytics();
      setEquation(equationObj.expression)
      setEquationWithId(equationObj.expressionWithId);
    } else if (buildFor === EquationBuilderFor.KPI) {
      fetchKPIDatapoints();
      fetchKPIMetrics();
      setEquation(equationObj.expression)
      setEquationWithId(equationObj.expressionWithId);
    } else {
      let req;
      if (buildFor === EquationBuilderFor.ESG_DATASET) {
        req = ESGDataSetService.getEquation(topicId);
      } else {
        req = EquationService.getEquationConfig(stdId, metricId, topicId, forCompany ? false : authority.roles.includes(USER_ROLE.SUPER_ADMIN));
      }
      req.then((response) => {
        setConfigDPData([...response.data.data.dataPoints]);
        setConfigMetricData([...response.data.data.metrics]);
        setConfigGDData(response.data.data.generalDisclosures ?? []);
        if (equationObj != null && equationObj !== undefined) {
          if (Array.isArray(equationObj)) {
            if (equationObj.length == 0) {
              return
            }
          }
          setEquation(equationObj.expression)
          setEquationWithId(equationObj.expressionWithId);
          setEditId(equationObj.id);
        }
      })
        .catch((error) => {
          console.error('Error fetching equation config:', error);
          toast.error(t("EquationBuilder.error.configFetch.label"), {
            position: toast.POSITION.TOP_RIGHT,
          });
        });
    }
  };

  const handleEquation = (val, e) => {
    e.preventDefault();
    var equationData = equation + val;
    var equationWithIdData = equationwithid + val;
    setEquation(equationData);
    setEquationWithId(equationWithIdData);
  };

  const validateBrackets = (formula) => {
    const stack = [];
    const brackets = { '(': ')', '[': ']', '{': '}' };

    for (let char of formula) {
      if (brackets[char]) {
        stack.push(char);
      } else if (Object.values(brackets).includes(char)) {
        if (stack.length === 0) {
          return false; // Unmatched closing bracket
        }
        const lastOpen = stack.pop();
        if (brackets[lastOpen] !== char) {
          return false; // Mismatched brackets
        }
      }
    }

    return stack.length === 0; // All brackets should be matched
  };

  const validateIFCondition = (formula) => {
    // Check for basic IF-THEN-ELSE structure
    const ifMatches = formula.match(/IF\s*\(/gi);
    const thenMatches = formula.match(/THEN\s*\(/gi);
    const elseMatches = formula.match(/ELSE\s*\(/gi);

    // Count occurrences
    const ifCount = ifMatches ? ifMatches.length : 0;
    const thenCount = thenMatches ? thenMatches.length : 0;
    const elseCount = elseMatches ? elseMatches.length : 0;

    // Basic structure validation
    if (ifCount === 0) {
      return { isValid: true, message: '' }; // No IF statements to validate
    }

    if (ifCount !== thenCount) {
      return {
        isValid: false,
        message: t("EquationBuilder.error.ifThenMismatch.label")
      };
    }

    // Check for proper nesting and condition structure
    const ifPattern = /IF\s*\(([^)]*)\)/gi;
    let match;
    let conditionIndex = 0;

    while ((match = ifPattern.exec(formula)) !== null) {
      const condition = match[1].trim();

      // Validate condition content
      if (!condition) {
        return {
          isValid: false,
          message: t("EquationBuilder.error.emptyIfCondition.label")
        };
      }

      // Check for basic condition operators
      const hasOperator = /[<>=!+\-*/]/.test(condition);
      if (!hasOperator) {
        return {
          isValid: false,
          message: t("EquationBuilder.error.invalidIfCondition.label")
        };
      }

      // Check for balanced parentheses in condition
      let parenCount = 0;
      for (let char of condition) {
        if (char === '(') parenCount++;
        if (char === ')') parenCount--;
        if (parenCount < 0) {
          return {
            isValid: false,
            message: t("EquationBuilder.error.unbalancedConditionParens.label")
          };
        }
      }

      // Additional validation for condition syntax
      const conditionParts = condition.split(/[<>=!+\-*/]/);
      if (conditionParts.length < 2) {
        return {
          isValid: false,
          message: t("EquationBuilder.error.invalidIfCondition.label")
        };
      }

      // Check if condition has proper left and right operands
      const leftOperand = conditionParts[0].trim();
      const rightOperand = conditionParts[conditionParts.length - 1].trim();

      if (!leftOperand || !rightOperand) {
        return {
          isValid: false,
          message: t("EquationBuilder.error.invalidIfCondition.label")
        };
      }

      conditionIndex++;
    }

    // Check for proper THEN-ELSE pairing
    const thenElsePattern = /THEN\s*\(([^)]*)\)\s*ELSE\s*\(([^)]*)\)/gi;
    const thenElseMatches = formula.match(thenElsePattern);

    if (thenElseMatches && thenElseMatches.length !== ifCount) {
      return {
        isValid: false,
        message: t("EquationBuilder.error.incompleteIfStructure.label")
      };
    }

    // Check for nested IF statements (optional validation)
    const nestedIfPattern = /IF\s*\([^)]*IF\s*\(/gi;
    if (nestedIfPattern.test(formula)) {
      // This is a warning rather than an error
      return {
        isValid: true,
        message: t("EquationBuilder.warning.nestedIf.label")
      };
    }

    return { isValid: true, message: '' };
  };

  const validateEquation = (formula) => {
    const normalized = stripEquationWhitespace(formula);
    if (!normalized) {
      return { isValid: true, message: '' };
    }
    // First validate brackets
    if (!validateBrackets(normalized)) {
      return {
        isValid: false,
        message: t("EquationBuilder.error.unmatchedBrackets.label")
      };
    }

    // Then validate IF conditions
    const ifValidation = validateIFCondition(normalized);
    if (!ifValidation.isValid) {
      return ifValidation;
    }

    return { isValid: true, message: '' };
  };

  const handleChange = (selectedValue, type) => {
    let filter = [];
    if (type == 'M') {
      filter = configMetricdata.filter(x => x.id == selectedValue);
      const metricIds = JSON.parse(JSON.stringify(selectedMetricIds));
      const mIndex = metricIds?.indexOf(filter[0].id);
      if (mIndex >= 0) {
        metricIds.splice(mIndex, 1);
      } else {
        metricIds.push(filter[0].id);
      }
      setSelectedMetricIds(metricIds);
    } else if (type === 'D') {
      filter = configDPdata.filter(x => x.id == selectedValue);
      const dps = JSON.parse(JSON.stringify(selectedDPs));
      const dpIndex = dps?.findIndex(d => d.id === filter[0].id);
      if (dpIndex >= 0) {
        dps.splice(dpIndex, 1);
      } else {
        dps.push(filter[0]);
      }
      setSelectedDPs(dps);
    } else if (type === 'G') {
      filter = configGDData.filter(x => x.id == selectedValue);
    } else if (type === 'N') {
      filter = configMetricdata.filter(x => x.id == selectedValue);
      const metricIds = JSON.parse(JSON.stringify(selectedMetricIds));
      const mIndex = metricIds?.indexOf(filter[0].id);
      if (mIndex >= 0) {
        metricIds.splice(mIndex, 1);
      } else {
        metricIds.push(filter[0].id);
      }
      setSelectedMetricIds(metricIds);
    } else if (type === 'E') {
      filter = configDPdata.filter(x => x.id == selectedValue);
      const dps = JSON.parse(JSON.stringify(selectedDPs));
      const dpIndex = dps?.findIndex(d => d.id === filter[0].id);
      if (dpIndex >= 0) {
        dps.splice(dpIndex, 1);
      } else {
        dps.push(filter[0]);
      }
      setSelectedDPs(dps);
    }
    if (filter[0]) {
      handleConfigEquation(filter[0].code, filter[0].id, type)
    }
  };

  const handleConfigEquation = (val, id, type) => {
    val = val.trim();
    let equationWithIdData;
    if (buildFor === EquationBuilderFor.GRAPH_CONFIG) {
      equationWithIdData = equationwithid + type + val.replace('-', 'XYZ');
    } else {
      equationWithIdData = equationwithid + type + val.replace('-', '#');
    }
    let equationData = equation + val;
    setEquation(equationData);
    setEquationWithId(equationWithIdData);
  };

  const saveData = () => {
    // Validate equation including IF conditions before saving
    const validation = validateEquation(equation);
    if (!validation.isValid) {
      toast.error(validation.message, {
        position: toast.POSITION.TOP_RIGHT,
      });
      return;
    }

    const body = {
      id: editid,
      expression: stripEquationWhitespace(equation),
      expressionWithId: stripEquationWhitespace(equationwithid)
    };
    if (buildFor === EquationBuilderFor.GRAPH_CONFIG
      || buildFor === EquationBuilderFor.KPI
      || buildFor === EquationBuilderFor.ESG_ANALYTICS) {
      body['selectedDPs'] = selectedDPs;
      body['selectedMetricIds'] = selectedMetricIds;
      setEquation(body.expression);
      setEquationWithId(body.expressionWithId);
      updatedEquationObj(body);
      return;
    }
    let req;
    if (buildFor === EquationBuilderFor.ESG_DATASET) {
      if (!body.id) {
        body.id = null;
      }
      req = ESGDataSetService.saveEquation(metricId, body);
    } else {
      req = EquationService.createEquation(metricId, body);
    }
    req.then((response) => {
      if (response.status === 200) {
        toast.success(t("EquationBuilder.success.label"), {
          position: toast.POSITION.TOP_RIGHT,
        });
        setEquation(response.data.data.expression);
        setEquationWithId(response.data.data.expressionWithId)
        setEditId(response.data.data.id)
        updatedEquationObj(response.data.data);
        discardBtnClick();
      }
    })
      .catch((error) => {
        console.error('Error saving equation:', error);
        toast.error(t("EquationBuilder.error.save.label"), {
          position: toast.POSITION.TOP_RIGHT,
        });
      });
  };

  const clearEquation = (e) => {
    e.preventDefault();
    setEquation('');
    setEquationWithId('');
  };

  /**
   * Converts equation with names to equation with IDs
   * Maps data point names and metric names back to their IDs
   */
  const convertEquationToWithId = (equationString) => {
    let equationWithId = equationString;

    // Create a mapping of names to IDs for all available data points and metrics
    const nameToIdMap = new Map();

    // Add data points mapping
    configDPdata.forEach(dp => {
      if (dp.code) {
        nameToIdMap.set(dp.code, `D${dp.code}`);
      }
    });

    // Add metrics mapping
    configMetricdata.forEach(metric => {
      if (metric.code) {
        nameToIdMap.set(metric.code, `M${metric.code}`);
      }
    });

    // Add general disclosures mapping
    configGDData.forEach(gd => {
      if (gd.code) {
        nameToIdMap.set(gd.code, `G${gd.code}`);
      }
    });

    // Replace names with IDs in the equation
    nameToIdMap.forEach((id, name) => {
      // Use word boundary regex to ensure we only replace complete words
      const regex = new RegExp(`\\b${name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'g');
      equationWithId = equationWithId.replace(regex, id);
    });

    return equationWithId;
  };

  const handlePasteEquation = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!navigator.clipboard || typeof navigator.clipboard.readText !== 'function') {
      toast.error(t("EquationBuilder.error.paste.label"), {
        position: toast.POSITION.TOP_RIGHT,
      });
      return;
    }
    try {
      const text = await navigator.clipboard.readText();
      const pasted = text != null ? String(text).trim() : '';
      const normalized = stripEquationWhitespace(pasted);
      setEquation(normalized);
      setEquationWithId(convertEquationToWithId(normalized));
    } catch (err) {
      toast.error(t("EquationBuilder.error.paste.label"), {
        position: toast.POSITION.TOP_RIGHT,
      });
    }
  };

  const backspaceEquation = (e) => {
    e.preventDefault();

    const codePattern = /^(?!\d+$)\w+$/g;

    const parts = equation.split(/([+\-*/%()])/);
    const lastPart = parts[parts.length - 1];

    let updatedEquation = equation;

    if (codePattern.test(lastPart)) {
      // Remove the entire last part (word/identifier)
      const updatedParts = parts.slice(0, parts.length - 1);
      updatedEquation = updatedParts.join('');
    } else {
      // Remove just the last character
      updatedEquation = equation.slice(0, -1);
    }

    // Update equation and derive equationWithId from it
    setEquation(updatedEquation);
    setEquationWithId(convertEquationToWithId(updatedEquation));
  };

  const replaceOperator = (oldOperator, newOperator) => {
    if (!oldOperator || !newOperator) return;

    const updatedEquation = equation.replace(oldOperator, newOperator);
    const updatedEquationWithId = equationwithid.replace(oldOperator, newOperator);

    setEquation(updatedEquation);
    setEquationWithId(updatedEquationWithId);
    setEditingOperator(null);
    setOperatorReplacement('');
  };

  const handleOperatorClick = (operator, startIndex, endIndex) => {
    setEditingOperator({ operator, startIndex, endIndex });
    setOperatorReplacement(operator);
  };

  const handleOperatorReplacement = () => {
    if (editingOperator && operatorReplacement) {
      replaceOperator(editingOperator.operator, operatorReplacement);
    }
  };

  const cancelOperatorEdit = () => {
    setEditingOperator(null);
    setOperatorReplacement('');
  };

  const handleKeyDown = (e) => {
    setIsKeyboardMode(true);

    switch (e.key) {
      case 'Tab':
        // Let default tab behavior work
        break;
      case 'ArrowLeft':
        e.preventDefault();
        navigateButtons('left');
        break;
      case 'ArrowRight':
        e.preventDefault();
        navigateButtons('right');
        break;
      case 'ArrowUp':
        e.preventDefault();
        navigateButtons('up');
        break;
      case 'ArrowDown':
        e.preventDefault();
        navigateButtons('down');
        break;
      case 'Enter':
        e.preventDefault();
        if (focusedButtonIndex >= 0 && buttonRefs.current[focusedButtonIndex]) {
          buttonRefs.current[focusedButtonIndex].click();
        }
        break;
      case ' ':
        e.preventDefault();
        handleEquation(' ', e);
        break;
      case 'Backspace':
        e.preventDefault();
        backspaceEquation(e);
        break;
      case 'Delete':
        e.preventDefault();
        clearEquation(e);
        break;
      case '1':
      case '2':
      case '3':
      case '4':
      case '5':
      case '6':
      case '7':
      case '8':
      case '9':
      case '0':
        e.preventDefault();
        handleEquation(e.key, e);
        break;
      case '+':
      case '-':
      case '*':
      case '/':
      case '%':
      case '(':
      case ')':
      case '.':
      case '=':
      case '!':
      case '<':
      case '>':
        e.preventDefault();
        handleEquation(e.key, e);
        break;
      default:
        break;
    }
  };

  const discardBtnData = () => {
    discardBtnClick();
  };

  const renderDataPointSelector = () => {
    return (
      <div className="col-lg-12 p-2">
        <h6 className="mb-1">{t("EquationBuilder.dataPoints.label")}</h6>
        <div className="d-flex align-items-center">
          <div className={buildFor === EquationBuilderFor.KPI ? 'w-90' : 'w-100'} >
            <div className="py-2">
              <input
                type="checkbox"
                className="form-check-input"
                id="dpCheck"
                checked={isDPEmission}
                onChange={() => setIsDPEmission(!isDPEmission)}
                aria-label={t("EquationBuilder.forEmission.label")}
              />
              <label className="form-check-label px-1" htmlFor="dpCheck">
                {t("EquationBuilder.forEmission.label")}
              </label>
            </div>
            <Typeahead
              id="dpSelector"
              key="dpSelector"
              clearButton
              selected={[]}
              labelKey={(option) => `(${option.code}) ${option.name ?? option.title}`}
              onChange={(selected) => {
                if (selected.length > 0) {
                  handleChange(selected[0].id, isDPEmission ? 'E' : 'D')
                }
              }}
              options={configDPdata}
              placeholder={t("Search.label")}
              aria-label={t("EquationBuilder.dataPoints.label")}
              renderMenuItemChildren={(option) => (
                <OverlayTrigger
                  placement="left"
                  overlay={
                    <Tooltip id={`tooltip-${option.id}`}>
                      {`(${option.code}) ${option.name ?? option.title}`}
                    </Tooltip>
                  }>
                  <div className="truncate-option">{`(${option.code}) ${option.name ?? option.title}`}</div>
                </OverlayTrigger>
              )} />
          </div>
          {
            buildFor === EquationBuilderFor.KPI &&
            <a
              className="text-primary cursor px-1"
              onClick={() => setDPModalDisplay(true)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setDPModalDisplay(true);
                }
              }}
              tabIndex={0}
              role="button"
              aria-label={t("EquationBuilder.addDataPoint.label")}
            >
              <MdAddCircleOutline />
            </a>
          }
        </div>
      </div>
    );
  };

  const renderMetricSelector = () => {
    return (
      <div className="col-lg-12 p-2">
        <h6 className="mb-1">{t("EquationBuilder.metrics.label")}</h6>
        <div className="py-2">
          <input
            type="checkbox"
            className="form-check-input"
            id="mtrCheck"
            checked={isMetricEmission}
            onChange={() => setIsMetricEmission(!isMetricEmission)}
            aria-label={t("EquationBuilder.forEmission.label")}
          />
          <label className="form-check-label px-1" htmlFor="mtrCheck">
            {t("EquationBuilder.forEmission.label")}
          </label>
        </div>
        <Typeahead
          
          id="metricSelector"
          key="metricSelector"
          clearButton
          selected={[]}
          labelKey={(option) => `(${option.code}) ${option.name}`}
          onChange={(selected) => {
            if (selected.length > 0) {
              handleChange(selected[0].id, isMetricEmission ? 'N' : 'M')
            }
          }}
          options={configMetricdata}
          placeholder={t("Search.label")}
          aria-label={t("EquationBuilder.metrics.label")}
          renderMenuItemChildren={(option) => (
            <OverlayTrigger
              placement="left"
              overlay={
                <Tooltip id={`tooltip-${option.id}`}>
                  {`(${option.code}) ${option.name}`}
                </Tooltip>
              }>
              <div className="truncate-option">{`(${option.code}) ${option.name}`}</div>
            </OverlayTrigger>
          )} />
      </div>
    );
  };

  const renderGDQuestionSelector = () => {
    if (configGDData?.length === 0) return <></>;

    return (
      <div className="col-lg-12 p-2">
        <h6 className="mb-1">{t('GDQuestions.label')}</h6>
        <Typeahead
          id="gdSelector"
          key="gdSelector"
          clearButton
          selected={[]}
          labelKey={(option) => `(${option.code}) ${option.name}`}
          onChange={(selected) => {
            if (selected.length > 0) {
              handleChange(selected[0].id, 'G')
            }
          }}
          options={configGDData}
          placeholder={t("Search.label")}
          aria-label={t('GDQuestions.label')}
          renderMenuItemChildren={(option) => (
            <OverlayTrigger
              placement="left"
              overlay={
                <Tooltip id={`tooltip-${option.id}`}>
                  {`(${option.code}) ${option.name}`}
                </Tooltip>
              }>
              <div className="truncate-option">{`(${option.code}) ${option.name}`}</div>
            </OverlayTrigger>
          )} />
      </div>
    );
  };

  const renderDPModal = () => {
    return (
      <Modal show={true} size="lg" onHide={() => setDPModalDisplay(false)} aria-labelledby="contained-modal-title-vcenter"
        centered>
        <Modal.Header closeButton>
          <Modal.Title as="h5">{t('NewDP.label')} </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <CreateDataPoint
            handleDiscard={() => setDPModalDisplay(false)}
            handleSave={() => {
              setDPModalDisplay(false);
              fetchKPIDatapoints();
            }}
            forKPI={true} />
        </Modal.Body>
      </Modal>
    );
  };

  const fetchKPIDatapoints = () => {
    KPIService.listKPIDatapoints(authority.profile.company.id).then(res => {
      if (res.status !== 200) return;

      setConfigDPData(res.data?.data ?? []);
    }).catch((error) => {
      console.error('Error fetching KPI datapoints:', error);
    });
  };

  const fetchKPIMetrics = () => {
    KPIService.getKPIMetrics().then(res => {
      if (res.status !== 200) return;

      setConfigMetricData(res.data ?? []);
    }).catch(err => {
      console.error('Error fetching KPI metrics:', err);
    });
  };

  const loadAllMetricsAndDatapointsForEnalytics = () => {
    StandardService.getAllMetricsAndDatapoints(stdId, null, topicIds)
      .then(res => {
        if (res.status !== 200) return;

        const metrics = res.data?.metrics ?? [];
        const datapoints = res.data?.dataPoints ?? [];
        setConfigDPData(datapoints);
        setConfigMetricData(metrics);
      })
      .catch(err => {
      });
  };

  return (
    <>
      {
        <div
          className="EquationBuilder"
          data-testid="equation-builder"
          ref={containerRef}
          role="application"
          aria-label={t("EquationBuilder.title.label")}
          tabIndex={-1}
        >
          <>
            <div>
              <div className="EquationBuilder_rowbox">
                <div className="EquationBuilder_rowbox_head">
                  <div className="editable-equation-display">
                    <div
                      className={`equation-display-with-paste ${!validationStatus.isValid ? 'border-danger' : ''}`}
                    >
                      <div
                        className="equation-display"
                        ref={equationDisplayRef}
                        tabIndex={0}
                        onKeyDown={handleKeyDown}
                        onKeyUp={(e) => {
                          if (e.key === 'Tab') {
                            setIsKeyboardMode(false);
                          }
                        }}
                        role="textbox"
                        aria-label={t("EquationBuilder.equationDisplay.label")}
                        aria-live="polite"
                        aria-atomic="true"
                      >
                        {(() => {
                          const parts = equation.split(/([+\-*/%()<>=!])/);
                          let currentIndex = 0;

                          return parts.map((part, index) => {
                            if (/[+\-*/%()<>=!]/.test(part)) {
                              const startIndex = currentIndex;
                              const endIndex = currentIndex + part.length;
                              currentIndex += part.length;

                              return (
                                <span
                                  key={index}
                                  className="clickable-operator"
                                >
                                  {part}
                                </span>
                              );
                            }
                            currentIndex += part.length;
                            return <span key={index}>{part}</span>;
                          });
                        })()}
                      </div>
                      <OverlayTrigger
                        placement="top"
                        overlay={(
                          <Tooltip id="equation-paste-tooltip">
                            {t("EquationBuilder.paste.tooltip.label")}
                          </Tooltip>
                        )}
                      >
                        <button
                          type="button"
                          className="equation-paste-btn"
                          onClick={handlePasteEquation}
                          aria-label={t("EquationBuilder.paste.label")}
                          data-testid="equation-paste-button"
                        >
                          <MdContentPaste className="equation-paste-icon" aria-hidden />
                        </button>
                      </OverlayTrigger>
                    </div>
                  </div>
                  {!validationStatus.isValid && (
                    <div className="text-danger small mt-1" role="alert" aria-live="assertive">
                      {validationStatus.message}
                    </div>
                  )}
                  {validationStatus.isValid && validationStatus.message && (
                    <div className="text-warning small mt-1" role="alert" aria-live="polite">
                      {validationStatus.message}
                    </div>
                  )}
                </div>
                <div className="EquationBuilder_rowbox_body">
                  <div className="row m-0">
                    <div className="col-lg-6 p-0">
                      <div className="EquationBuilder_number 1 col-lg-12  p-0">
                        <ul role="group" aria-label={t("EquationBuilder.calculator.label")}>
                          <li>
                            <Button
                              variant="light1"
                              className="btn px-3"
                              onClick={(e) => handleEquation('(', e)}
                              ref={el => buttonRefs.current[0] = el}
                              aria-label={t("EquationBuilder.leftParenthesis.label")}
                              tabIndex={0}
                            >(</Button>
                          </li>
                          <li>
                            <Button
                              variant="light1"
                              className="btn px-3"
                              onClick={(e) => handleEquation(')', e)}
                              ref={el => buttonRefs.current[1] = el}
                              aria-label={t("EquationBuilder.rightParenthesis.label")}
                              tabIndex={0}
                            >)</Button>
                          </li>
                          <li>
                            <Button
                              variant="light1"
                              className="btn px-3"
                              onClick={(e) => handleEquation('%', e)}
                              ref={el => buttonRefs.current[2] = el}
                              aria-label={t("EquationBuilder.percent.label")}
                              tabIndex={0}
                            >%</Button>
                          </li>
                          <li>
                            <Button
                              variant="light1"
                              className="btn px-3"
                              data-testid="backspace-button"
                              onClick={(e) => backspaceEquation(e)}
                              ref={el => buttonRefs.current[3] = el}
                              aria-label={t("EquationBuilder.backspace.label")}
                              tabIndex={0}
                            >
                              <MdBackspace />
                            </Button>
                          </li>

                          <li>
                            <Button
                              variant="light"
                              className="btn px-3"
                              onClick={(e) => handleEquation(1, e)}
                              ref={el => buttonRefs.current[4] = el}
                              aria-label={t("EquationBuilder.number1.label")}
                              tabIndex={0}
                            >1</Button>
                          </li>
                          <li>
                            <Button
                              variant="light"
                              className="btn px-3"
                              onClick={(e) => handleEquation(2, e)}
                              ref={el => buttonRefs.current[5] = el}
                              aria-label={t("EquationBuilder.number2.label")}
                              tabIndex={0}
                            >2</Button>
                          </li>
                          <li>
                            <Button
                              variant="light"
                              className="btn px-3"
                              onClick={(e) => handleEquation(3, e)}
                              ref={el => buttonRefs.current[6] = el}
                              aria-label={t("EquationBuilder.number3.label")}
                              tabIndex={0}
                            >3</Button>
                          </li>

                          <li>
                            <Button
                              variant="light1"
                              className="btn px-3"
                              onClick={(e) => clearEquation(e)}
                              ref={el => buttonRefs.current[7] = el}
                              aria-label={t("EquationBuilder.clear.label")}
                              tabIndex={0}
                            >
                              AC
                            </Button>
                          </li>

                          <li>
                            <Button
                              variant="light"
                              className="btn px-3"
                              onClick={(e) => handleEquation(4, e)}
                              ref={el => buttonRefs.current[8] = el}
                              aria-label={t("EquationBuilder.number4.label")}
                              tabIndex={0}
                            >4</Button>
                          </li>
                          <li>
                            <Button
                              variant="light"
                              className="btn px-3"
                              onClick={(e) => handleEquation(5, e)}
                              ref={el => buttonRefs.current[9] = el}
                              aria-label={t("EquationBuilder.number5.label")}
                              tabIndex={0}
                            >5</Button>
                          </li>
                          <li>
                            <Button
                              variant="light"
                              className="btn px-3"
                              onClick={(e) => handleEquation(6, e)}
                              ref={el => buttonRefs.current[10] = el}
                              aria-label={t("EquationBuilder.number6.label")}
                              tabIndex={0}
                            >6</Button>
                          </li>

                          <li>
                            <Button
                              variant="light1"
                              className="btn px-3"
                              onClick={(e) => handleEquation('/', e)}
                              ref={el => buttonRefs.current[11] = el}
                              aria-label={t("EquationBuilder.divide.label")}
                              tabIndex={0}
                            >/</Button>
                          </li>

                          <li>
                            <Button
                              variant="light"
                              className="btn px-3"
                              onClick={(e) => handleEquation(7, e)}
                              ref={el => buttonRefs.current[12] = el}
                              aria-label={t("EquationBuilder.number7.label")}
                              tabIndex={0}
                            >7</Button>
                          </li>
                          <li>
                            <Button
                              variant="light"
                              className="btn px-3"
                              onClick={(e) => handleEquation(8, e)}
                              ref={el => buttonRefs.current[13] = el}
                              aria-label={t("EquationBuilder.number8.label")}
                              tabIndex={0}
                            >8</Button>
                          </li>
                          <li>
                            <Button
                              variant="light"
                              className="btn px-3"
                              onClick={(e) => handleEquation(9, e)}
                              ref={el => buttonRefs.current[14] = el}
                              aria-label={t("EquationBuilder.number9.label")}
                              tabIndex={0}
                            >9</Button>
                          </li>

                          <li>
                            <Button
                              variant="light1"
                              className="btn px-3 multiplication_icon"
                              onClick={(e) => handleEquation('*', e)}
                              ref={el => buttonRefs.current[15] = el}
                              aria-label={t("EquationBuilder.multiply.label")}
                              tabIndex={0}
                            >*</Button>
                          </li>

                          <li>
                            <Button
                              variant="light"
                              className="btn px-3"
                              onClick={(e) => handleEquation(0, e)}
                              ref={el => buttonRefs.current[16] = el}
                              aria-label={t("EquationBuilder.number0.label")}
                              tabIndex={0}
                            >0</Button>
                          </li>
                          <li>
                            <Button
                              variant="light"
                              className="btn px-3 dotsing_icon"
                              onClick={(e) => handleEquation('.', e)}
                              ref={el => buttonRefs.current[17] = el}
                              aria-label={t("EquationBuilder.decimal.label")}
                              tabIndex={0}
                            >.</Button>
                          </li>
                          <li>
                            <Button
                              variant="light"
                              className="btn px-3"
                              onClick={(e) => handleEquation('=', e)}
                              ref={el => buttonRefs.current[18] = el}
                              aria-label={t("EquationBuilder.equals.label")}
                              tabIndex={0}
                            >=</Button>
                          </li>

                          <li>
                            <Button
                              variant="light1"
                              className="btn px-3 plussign_icon"
                              onClick={(e) => handleEquation('+', e)}
                              ref={el => buttonRefs.current[19] = el}
                              aria-label={t("EquationBuilder.add.label")}
                              tabIndex={0}
                            >+</Button>
                          </li>


                          <li>
                            <Button
                              variant="light1"
                              className="btn px-3 minussign_icon"
                              onClick={(e) => handleEquation('!', e)}
                              ref={el => buttonRefs.current[23] = el}
                              aria-label={t("EquationBuilder.subtract.label")}
                              tabIndex={0}
                            >!</Button>
                          </li>

                          <li>
                            <Button
                              variant="light"
                              className="btn px-3"
                              onClick={(e) => handleEquation('<', e)}
                              ref={el => buttonRefs.current[24] = el}
                              aria-label={t("EquationBuilder.lessThan.label")}
                              tabIndex={0}
                            ><BiChevronLeft /></Button>
                          </li>
                          <li>
                            <Button
                              variant="light"
                              className="btn px-3"
                              onClick={(e) => handleEquation('>', e)}
                              ref={el => buttonRefs.current[27] = el}
                              aria-label={t("EquationBuilder.greaterThan.label")}
                              tabIndex={0}
                            ><BiChevronRight /></Button>
                          </li>
                          <li>
                            <Button
                              variant="light1"
                              className="btn px-3 minussign_icon"
                              onClick={(e) => handleEquation('-', e)}
                              ref={el => buttonRefs.current[23] = el}
                              aria-label={t("EquationBuilder.subtract.label")}
                              tabIndex={0}
                            >-</Button>
                          </li>

                        </ul>
                        <ul className="EquationBuilder_number 1 col-lg-12  p-0">
                          <li>
                            <Button
                              variant="light"
                              className="btn px-3"
                              onClick={(e) => handleEquation('IF(', e)}
                              ref={el => buttonRefs.current[20] = el}
                              aria-label={t("EquationBuilder.ifFunction.label")}
                              tabIndex={0}
                            >IF</Button>
                          </li>
                          <li>
                            <Button
                              variant="light"
                              className="btn px-3"
                              onClick={(e) => handleEquation('THEN(', e)}
                              ref={el => buttonRefs.current[21] = el}
                              aria-label={t("EquationBuilder.thenFunction.label")}
                              tabIndex={0}
                            >THEN</Button>
                          </li>
                          <li>
                            <Button
                              variant="light"
                              className="btn px-3"
                              onClick={(e) => handleEquation('ELSE(', e)}
                              ref={el => buttonRefs.current[22] = el}
                              aria-label={t("EquationBuilder.elseFunction.label")}
                              tabIndex={0}
                            >ELSE</Button>
                          </li>
                        </ul>
                      </div>

                    </div>
                    <div className="col-lg-6 p-0">
                      {renderDataPointSelector()}
                      {renderMetricSelector()}
                      {renderGDQuestionSelector()}
                    </div>
                  </div>
                </div>
              </div>

              <div className="text-center mt-2 EquationBuilder_rowbox_footer">
                <button
                  className="btn btn-secondary px-3 me-2"
                  onClick={discardBtnData}
                  aria-label={t("EquationBuilder.discard.label")}
                >
                  {t("Discard.label")}
                </button>
                <button
                  className="btn btn-primary px-3"
                  onClick={saveData}
                  aria-label={t("EquationBuilder.save.label")}
                >
                  {equationObj || buildFor === EquationBuilderFor.KPI || buildFor === EquationBuilderFor.ESG_ANALYTICS
                    ? <>{t("Update.label")}</>
                    : <>{t("Create.label")}</>
                  }
                </button>
              </div>
            </div>
          </>
        </div>
      }
      {dpModalDisplay && renderDPModal()}
    </>
  );
}
export default withTranslation()(EquationBuilder);
